import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useProducts, useCollections } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { ProductGrid } from "@/components/shop/ProductCard";
import { SplitText } from "@/components/fx/Motion";

const TITLES = { primus: "Primus", bellecare: "Bellecare Natural" };
const SORTS = [["featured", "sort_featured"], ["price-asc", "sort_price_asc"], ["price-desc", "sort_price_desc"], ["discount", "sort_discount"], ["newest", "sort_newest"]];

export default function Shop() {
  const { handle } = useParams();
  const { t } = useI18n();
  const [sort, setSort] = useState("featured");
  const { data = [], isLoading, isError } = useProducts({ collection: handle, sort });
  const { data: cols = [] } = useCollections();
  const tabs = [{ handle: undefined, title: t("filter_all") }, ...cols.filter((c) => c.handle !== "frontpage")];
  const title = handle ? (handle === "frontpage" ? t("bestsellers") : TITLES[handle] || handle) : t("shop_all_title");
  useSeo(handle ? title : t("seo_shop"));

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20">
      <p className="eyebrow mb-4">{t("shop_eyebrow")}</p>
      <h1 data-testid="shop-title" className="font-display text-4xl sm:text-5xl lg:text-7xl mb-10"><SplitText key={title} text={title} /></h1>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 border-y border-white/10 py-4 mb-12">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <Link key={tab.handle || "all"} to={tab.handle ? `/collections/${tab.handle}` : "/shop"} data-testid={`shop-filter-${tab.handle || "all"}`}
              className={`px-5 h-10 flex items-center text-xs uppercase tracking-[0.18em] border whitespace-nowrap transition-colors duration-300 ${handle === tab.handle ? "bg-gold text-[#0b0a08] border-transparent font-semibold" : "border-white/15 text-white/70 hover:border-[#c9a55c]"}`}>
              {tab.title}{tab.count ? <span className="ml-2 opacity-60">{tab.count}</span> : null}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <span data-testid="shop-product-count" className="text-xs text-white/50">{t("n_products", { n: data.length })}</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="shop-sort-select" className="w-[200px] rounded-none bg-transparent border-white/15"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0e0d0b] border-white/10">
              {SORTS.map(([v, k]) => <SelectItem key={v} value={v} data-testid={`shop-sort-${v}`}>{t(k)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {isError ? <p className="text-white/60">{t("load_error")}</p> : <ProductGrid key={`${handle}-${sort}`} products={data} loading={isLoading} />}
    </div>
  );
}
